import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type {
  BFFArticleBodyElement,
  BFFArticleCard,
  BFFResponseType,
} from "@src/app/types/bff";
import type { ArticlePageResponse } from "@src/app/types/response";
import { useEffect, useState } from "react";

import NewsletterSignUp from "../NewsletterSignUp";
import ArticleEmbed from "./body/ArticleEmbed";
import ArticleImage from "./body/ArticleImage";
import ArticleListBulleted from "./body/ArticleListBulleted";
import ArticleListNumbered from "./body/ArticleListNumbered";
import ArticleMedia from "./body/ArticleMedia";
import ArticleParagraph from "./body/ArticleParagraph";
import ArticlePullQuote from "./body/ArticlePullQuote";
import ArticleRelation from "./body/ArticleRelation";
import ArticleSubHead from "./body/ArticleSubHead";
import ArticleYoutube from "./body/ArticleYoutube";
import ComicLightbox from "./body/components/ArticleComicLightBox";

export const mapBodyElementLayout = (
  element: BFFArticleBodyElement[keyof BFFArticleBodyElement],
  options?: {
    /** latest articles in Relation Element */
    relationArticles?: BFFArticleCard[];
  },
) => {
  if (element.type == "paragraph") {
    return <ArticleParagraph element={element} />;
  }
  if (element.type == "image") {
    return <ArticleImage element={element} />;
  }
  if (element.type == "embed") {
    return <ArticleEmbed element={element} />;
  }
  if (element.type == "sub_head") {
    return <ArticleSubHead element={element} />;
  }
  if (element.type == "youtube_video") {
    return <ArticleYoutube element={element} />;
  }
  if (element.type == "list_bulleted") {
    return <ArticleListBulleted element={element} />;
  }
  if (element.type == "list_numbered") {
    return <ArticleListNumbered element={element} />;
  }
  if (element.type == "pull_quote") {
    return <ArticlePullQuote element={element} />;
  }
  if (element.type == "media") {
    return <ArticleMedia element={element} />;
  }

  // Uncomment if map support is added in the future
  // if (element.type == "map") {
  //   return <ArticleMap element={element} />;
  // }
  // if (element.type == "table") {
  //   return <ArticleTable element={element} />;
  // }
  // if (element.type == "interview") {
  //   return <ArticleInterview element={element} />;
  // }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (element.type == "relation") {
    return (
      <ArticleRelation
        element={element}
        relationArticles={options?.relationArticles || []}
      />
    );
  }
  return null;
};

export default function ArticleBody() {
  const { article } = useRouteResponse("articlePage");

  // feature & comic article
  const isFullWidthArticle = ["comic", "photo_story", "big_read"].includes(
    article?.displayType || "",
  );

  // regional article
  const isRegionalArticle = article?.tags.some(
    (tag) => tag.urlPath === "/tags/thinkchina-regional-series",
  );

  // set newsletter variant based on article type
  const newsletterVariant = isRegionalArticle
    ? "regionalCard"
    : article?.section === "culture"
      ? "cultureCard"
      : "card";

  const { bodyElements = [] } = article || {};

  const [relationArticles, setRelationArticles] = useState<BFFArticleCard[]>(
    [],
  );

  const getInfo = async () => {
    if (!article?.id) {
      console.warn("No article ID found for fetching recommended articles.");
      return;
    }
    try {
      const res = await fetch(
        `/_plat/api/v2/client/relationArticles/${article.id}`,
      );
      const data = (await res.json()) as BFFResponseType<ArticlePageResponse>;

      setRelationArticles(data.response.relationArticles || []);
    } catch (error) {
      console.error("Error fetching relation articles:", error);
    }
  };

  useEffect(() => {
    void getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if comic articles only display image elements
  if (article?.displayType == "comic") {
    const imageElements = bodyElements.filter((i) => i.type == "image");
    return (
      <ComicLightbox
        images={imageElements.map((i) => ({ src: i.url, alt: i.altText }))}
      />
    );
  }

  const midArticleIndex = Math.ceil(bodyElements.length / 2);

  return bodyElements.flatMap((element, index) => {
    if (index == midArticleIndex && !isFullWidthArticle) {
      return [
        <div
          key={`${element.type}-${index}`}
          className="text-primary dark:text-primary-on-dark mx-3"
        >
          {mapBodyElementLayout(element, { relationArticles })}
        </div>,
        <div className="lg:hidden" key="default-article-newsletter-signup">
          <NewsletterSignUp variant={newsletterVariant} withIcon={false} />
        </div>,
      ];
    }
    return (
      <div
        key={`${element.type}-${index}`}
        className="text-primary dark:text-primary-on-dark px-3"
      >
        {mapBodyElementLayout(element, { relationArticles })}
      </div>
    );
  });
}
